import React, { useState, useEffect } from 'react';
import './WorkReportForm.css';
import './WorkReportFormNew.css';
import { useTranslation } from 'react-i18next';
import {
  Employee,
  Machinery,
  Supplier,
  Truck,
  Material,
} from '../../types/catalogs';
import {
  fetchEmployees,
  fetchMachinery,
  fetchSuppliers,
  fetchTrucks,
  fetchMaterials,
} from '../../API/catalogsApi';
import { SearchableSelect, SearchableOption } from '../common/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';

// Interfaces adaptadas para usar IDs
interface PersonalPropioEntry {
  employeeId: number;
  horaInicio: string;
  horaFin: string;
  duracionParadas: number;
  jornadaBruta: number;
  jornadaNeta: number;
  agrupacion: string;
}

interface MaquinariaPropiaEntry {
  machineryId: number;
  dias: number;
}

interface MaquinariaAlquiladaEntry {
  maquina: string;
  supplierId: number | null;
  dias: number;
  horas: number;
}

interface TemperaturaMezclaEntry {
  camion: string;
  horaLlegada: string;
  temperatura: number;
}

interface CamionFresandoEntry {
  truckId: number | null;
  matriculaConductor: string;
  horas: number;
  viajes: number;
}

interface CamionObraEntry {
  truckId: number | null;
  matriculaConductor: string;
  horas: number;
}

interface MaterialExtendidoEntry {
  materialId: number;
  descripcionExtra: string;
  m2: number;
  tn: number;
}

interface FormData {
  fecha: string;
  enviadoPor: string;
  temperaturaAmbiente: number;
  ambiente: string;
  estadoSuperficie: string;
  personalPropio: PersonalPropioEntry[];
  maquinariaPropia: MaquinariaPropiaEntry[];
  maquinariaAlquilada: MaquinariaAlquiladaEntry[];
  temperaturaMezcla: TemperaturaMezclaEntry[];
  camionesFresando: CamionFresandoEntry[];
  camionesObra: CamionObraEntry[];
  materialesExtendido: MaterialExtendidoEntry[];
  observaciones: string;
  status: 'draft' | 'completed' | 'approved';
}

interface WorkReportFormProps {
  projectId: number;
  workReport?: any;
  onSave: () => void;
  onCancel: () => void;
}

export const WorkReportFormNew = ({ projectId, workReport, onSave, onCancel }: WorkReportFormProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const groupingLabel = (code: string) => {
    const labels: Record<string, string> = {
      EXTENDIDO: t('groupingLaid'),
      FRESADO: t('groupingMilling'),
      RIEGOS: t('groupingTackCoat'),
      COMPACTADO: t('groupingCompacted'),
      BARRIDO: t('groupingSwept'),
      SEÑALIZACION: t('groupingSignage'),
      RIEGO_AGLOMERADO: t('groupingAgglomerateCoat'),
    };
    return labels[code] || code;
  };

  // Estados para catálogos
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [machinery, setMachinery] = useState<Machinery[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [trucksFresado, setTrucksFresado] = useState<Truck[]>([]);
  const [trucksObra, setTrucksObra] = useState<Truck[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    fecha: new Date().toISOString().split('T')[0],
    enviadoPor: '',
    temperaturaAmbiente: 16,
    ambiente: 'DIRECTO',
    estadoSuperficie: 'DIRECTO',
    personalPropio: [],
    maquinariaPropia: [],
    maquinariaAlquilada: [],
    temperaturaMezcla: [],
    camionesFresando: [],
    camionesObra: [],
    materialesExtendido: [],
    observaciones: '',
    status: 'draft'
  });

  const [images, setImages] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState('general');

  // Cargar catálogos al montar el componente
  useEffect(() => {
    const loadCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const [empData, machData, suppData, trucksFresData, trucksObraData, matData] = await Promise.all([
          fetchEmployees(),
          fetchMachinery(),
          fetchSuppliers(),
          fetchTrucks('FRESADO'),
          fetchTrucks('OBRA'),
          fetchMaterials(),
        ]);

        setEmployees(empData);
        setMachinery(machData);
        setSuppliers(suppData);
        setTrucksFresado(trucksFresData);
        setTrucksObra(trucksObraData);
        setMaterials(matData);
      } catch (error) {
        console.error('Error loading catalogs:', error);
      } finally {
        setLoadingCatalogs(false);
      }
    };

    loadCatalogs();
  }, []);

  // Cargar datos del work report para edición
  useEffect(() => {
    if (workReport) {
      const data = workReport.attributes;

      setFormData({
        fecha: data.fecha || new Date().toISOString().split('T')[0],
        enviadoPor: data.enviadoPor || '',
        temperaturaAmbiente: data.temperaturaAmbiente || 16,
        ambiente: data.ambiente || 'DIRECTO',
        estadoSuperficie: data.estadoSuperficie || 'DIRECTO',
        personalPropio: (data.personalPropio || []).map((p: any) => ({
          employeeId: p.employee?.data?.id || 0,
          horaInicio: p.horaInicio || '08:00',
          horaFin: p.horaFin || '17:00',
          duracionParadas: p.duracionParadas || 1,
          jornadaBruta: p.jornadaBruta || 9,
          jornadaNeta: p.jornadaNeta || 8,
          agrupacion: p.agrupacion || 'EXTENDIDO'
        })),
        maquinariaPropia: (data.maquinariaPropia || []).map((m: any) => ({
          machineryId: m.machinery?.data?.id || 0,
          dias: m.dias || 1
        })),
        maquinariaAlquilada: (data.maquinariaAlquilada || []).map((m: any) => ({
          maquina: m.maquina || '',
          supplierId: m.supplier?.data?.id || null,
          dias: m.dias || 0,
          horas: m.horas || 0
        })),
        temperaturaMezcla: (data.temperaturaMezcla || []).map((t: any) => ({
          camion: t.camion || '',
          horaLlegada: t.horaLlegada || '',
          temperatura: t.temperatura || 0
        })),
        camionesFresando: (data.camionesFresando || []).map((c: any) => ({
          truckId: c.truck?.data?.id || null,
          matriculaConductor: c.matriculaConductor || '',
          horas: c.horas || 0,
          viajes: c.viajes || 0
        })),
        camionesObra: (data.camionesObra || []).map((c: any) => ({
          truckId: c.truck?.data?.id || null,
          matriculaConductor: c.matriculaConductor || '',
          horas: c.horas || 0
        })),
        materialesExtendido: (data.materialesExtendido || []).map((m: any) => ({
          materialId: m.material?.data?.id || 0,
          descripcionExtra: m.descripcionExtra || '',
          m2: m.m2 || 0,
          tn: m.tn || 0
        })),
        observaciones: data.observaciones || '',
        status: data.status || 'draft'
      });
    }
  }, [workReport]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Personal Propio functions
  const addPersonalPropio = () => {
    setFormData(prev => ({
      ...prev,
      personalPropio: [
        ...prev.personalPropio,
        {
          employeeId: 0,
          horaInicio: '08:00',
          horaFin: '17:00',
          duracionParadas: 1,
          jornadaBruta: 9,
          jornadaNeta: 8,
          agrupacion: 'EXTENDIDO'
        }
      ]
    }));
  };

  const updatePersonalPropio = (index: number, field: keyof PersonalPropioEntry, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      personalPropio: prev.personalPropio.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));

    // Auto-calcular jornadas cuando cambian los horarios
    if (field === 'horaInicio' || field === 'horaFin' || field === 'duracionParadas') {
      const personal = formData.personalPropio[index];
      const inicio = field === 'horaInicio' ? String(value) : personal.horaInicio;
      const fin = field === 'horaFin' ? String(value) : personal.horaFin;
      const paradas = field === 'duracionParadas' ? Number(value) : personal.duracionParadas;

      const jornadaBruta = calculateHoursDifference(inicio, fin);
      const jornadaNeta = jornadaBruta - paradas;

      setFormData(prev => ({
        ...prev,
        personalPropio: prev.personalPropio.map((item, i) =>
          i === index ? { ...item, jornadaBruta, jornadaNeta } : item
        )
      }));
    }
  };

  const calculateHoursDifference = (inicio: string, fin: string): number => {
    const [hInicio, mInicio] = inicio.split(':').map(Number);
    const [hFin, mFin] = fin.split(':').map(Number);
    const minutosInicio = hInicio * 60 + mInicio;
    const minutosFin = hFin * 60 + mFin;
    return Math.round((minutosFin - minutosInicio) / 60 * 10) / 10;
  };

  const removePersonalPropio = (index: number) => {
    setFormData(prev => ({
      ...prev,
      personalPropio: prev.personalPropio.filter((_, i) => i !== index)
    }));
  };

  // Maquinaria Propia functions
  const addMaquinariaPropia = () => {
    setFormData(prev => ({
      ...prev,
      maquinariaPropia: [
        ...prev.maquinariaPropia,
        { machineryId: 0, dias: 1 }
      ]
    }));
  };

  const updateMaquinariaPropia = (index: number, field: keyof MaquinariaPropiaEntry, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      maquinariaPropia: prev.maquinariaPropia.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeMaquinariaPropia = (index: number) => {
    setFormData(prev => ({
      ...prev,
      maquinariaPropia: prev.maquinariaPropia.filter((_, i) => i !== index)
    }));
  };

  // Maquinaria Alquilada
  const addMaquinariaAlquilada = () => {
    setFormData(prev => ({
      ...prev,
      maquinariaAlquilada: [
        ...prev.maquinariaAlquilada,
        { maquina: '', supplierId: null, dias: 1, horas: 8 }
      ]
    }));
  };

  // Temperatura Mezcla
  const addTemperaturaMezcla = () => {
    setFormData(prev => ({
      ...prev,
      temperaturaMezcla: [
        ...prev.temperaturaMezcla,
        { camion: '', horaLlegada: '09:00', temperatura: 170 }
      ]
    }));
  };

  // Camiones Fresando
  const addCamionFresando = () => {
    setFormData(prev => ({
      ...prev,
      camionesFresando: [
        ...prev.camionesFresando,
        { truckId: null, matriculaConductor: '', horas: 8, viajes: 1 }
      ]
    }));
  };

  // Camiones Obra
  const addCamionObra = () => {
    setFormData(prev => ({
      ...prev,
      camionesObra: [
        ...prev.camionesObra,
        { truckId: null, matriculaConductor: '', horas: 8 }
      ]
    }));
  };

  // Materiales Extendido
  const addMaterialExtendido = () => {
    setFormData(prev => ({
      ...prev,
      materialesExtendido: [
        ...prev.materialesExtendido,
        { materialId: 0, descripcionExtra: '', m2: 0, tn: 0 }
      ]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fecha) {
      newErrors.fecha = t('dateRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadFiles = async (files: File[], fieldName: string) => {
    if (files.length === 0) return [];

    const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
    const uploadedFiles = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('files', file);

      try {
        const response = await fetch(`${backendUrl}/api/upload`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const uploadResult = await response.json();
          uploadedFiles.push(...uploadResult.map((file: any) => file.id));
        }
      } catch (error) {
        console.error(`Error uploading ${fieldName}:`, error);
      }
    }

    return uploadedFiles;
  };

  // Función para convertir HH:mm a HH:mm:ss.SSS
  const formatTimeForStrapi = (time: string): string => {
    if (!time) return '';
    // Si ya tiene el formato completo, devolverlo tal cual
    if (time.includes('.')) return time;
    // Si tiene formato HH:mm:ss, agregar milisegundos
    if (time.split(':').length === 3) return `${time}.000`;
    // Si tiene formato HH:mm, agregar segundos y milisegundos
    return `${time}:00.000`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';

      const imageIds = await uploadFiles(images, 'images');
      const attachmentIds = await uploadFiles(attachments, 'attachments');

      // Preparar componentes para Strapi
      const personalPropioComponents = formData.personalPropio.map(p => ({
        employee: p.employeeId,
        horaInicio: formatTimeForStrapi(p.horaInicio),
        horaFin: formatTimeForStrapi(p.horaFin),
        duracionParadas: p.duracionParadas,
        jornadaBruta: p.jornadaBruta,
        jornadaNeta: p.jornadaNeta,
        agrupacion: p.agrupacion
      }));

      const maquinariaPropiaComponents = formData.maquinariaPropia.map(m => ({
        machinery: m.machineryId,
        dias: m.dias
      }));

      const maquinariaAlquiladaComponents = formData.maquinariaAlquilada.map(m => ({
        maquina: m.maquina,
        supplier: m.supplierId,
        dias: m.dias,
        horas: m.horas
      }));

      const temperaturaMezclaComponents = formData.temperaturaMezcla.map(t => ({
        camion: t.camion,
        horaLlegada: t.horaLlegada,
        temperatura: t.temperatura
      }));

      const camionesFresandoComponents = formData.camionesFresando.map(c => ({
        truck: c.truckId,
        matriculaConductor: c.matriculaConductor,
        horas: c.horas,
        viajes: c.viajes
      }));

      const camionesObraComponents = formData.camionesObra.map(c => ({
        truck: c.truckId,
        matriculaConductor: c.matriculaConductor,
        horas: c.horas
      }));

      const materialesExtendidoComponents = formData.materialesExtendido.map(m => ({
        material: m.materialId,
        descripcionExtra: m.descripcionExtra,
        m2: m.m2,
        tn: m.tn
      }));

      const data = {
        // El campo numero se auto-genera en el backend
        fecha: formData.fecha,
        project: projectId,
        enviadoPor: formData.enviadoPor,
        temperaturaAmbiente: formData.temperaturaAmbiente,
        ambiente: formData.ambiente,
        estadoSuperficie: formData.estadoSuperficie,
        personalPropio: personalPropioComponents,
        maquinariaPropia: maquinariaPropiaComponents,
        maquinariaAlquilada: maquinariaAlquiladaComponents,
        temperaturaMezcla: temperaturaMezclaComponents,
        camionesFresando: camionesFresandoComponents,
        camionesObra: camionesObraComponents,
        materialesExtendido: materialesExtendidoComponents,
        observaciones: formData.observaciones,
        status: formData.status,
        images: imageIds,
        attachments: attachmentIds
      };

      const url = workReport
        ? `${backendUrl}/api/work-reports/${workReport.id}`
        : `${backendUrl}/api/work-reports`;

      const method = workReport ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ data }),
      });

      if (response.ok) {
        onSave();
      } else {
        const errorData = await response.json();
        console.error('Error saving work report:', errorData);
        alert(`${t('saveItemError')}: ${errorData?.error?.message || t('unknownError')}`);
      }
    } catch (error) {
      console.error('Error saving work report:', error);
      alert(t('connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const getSectionIcon = (sectionName: string) => {
    switch (sectionName) {
      case 'general': return '📋';
      case 'personal': return '👥';
      case 'maquinaria': return '🚚';
      case 'materiales': return '🏗️';
      case 'condiciones': return '🌤️';
      default: return '📄';
    }
  };

  if (loadingCatalogs) {
    return (
      <div className="work-report-form">
        <div className="loading-catalogs">
          <p>{t('loadingCatalogs')}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="work-report-form">
      <div className="form-header">
        <h2>
          {workReport ? t('editWorkReport') : t('createWorkReportTitle')}
        </h2>
      </div>

      {/* Navigation Tabs */}
      <div className="form-tabs">
        <button
          type="button"
          className={activeSection === 'general' ? 'active' : ''}
          onClick={() => setActiveSection('general')}
        >
          {getSectionIcon('general')} {t('generalInfo')}
        </button>
        <button
          type="button"
          className={activeSection === 'personal' ? 'active' : ''}
          onClick={() => setActiveSection('personal')}
        >
          {getSectionIcon('personal')} {t('ownStaff')}
        </button>
        <button
          type="button"
          className={activeSection === 'maquinaria' ? 'active' : ''}
          onClick={() => setActiveSection('maquinaria')}
        >
          {getSectionIcon('maquinaria')} {t('catalogMachinery')}
        </button>
        <button
          type="button"
          className={activeSection === 'materiales' ? 'active' : ''}
          onClick={() => setActiveSection('materiales')}
        >
          {getSectionIcon('materiales')} {t('laidMaterials')}
        </button>
        <button
          type="button"
          className={activeSection === 'condiciones' ? 'active' : ''}
          onClick={() => setActiveSection('condiciones')}
        >
          {getSectionIcon('condiciones')} {t('conditionsNotes')}
        </button>
      </div>

      <div className="form-content">
        {/* General Information Section */}
        {activeSection === 'general' && (
          <div className="form-section">
            <h3>📋 {t('generalInfo')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="fecha">
                  {t('date')} <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="fecha"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  className={errors.fecha ? 'error' : ''}
                />
                {errors.fecha && <span className="error-text">{errors.fecha}</span>}
                <small className="help-text">{t('reportNumberAuto')}</small>
              </div>

              <div className="form-group">
                <label htmlFor="enviadoPor">{t('sentBy')}</label>
                <input
                  type="text"
                  id="enviadoPor"
                  name="enviadoPor"
                  value={formData.enviadoPor}
                  onChange={handleInputChange}
                  placeholder="ANGEL TERRONES"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">{t('state')}</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="draft">{t('draft')}</option>
                  <option value="completed">{t('completed')}</option>
                  <option value="approved">{t('approved')}</option>
                </select>
              </div>

              <div className="form-group full-width">
                <h4>{t('weather')}</h4>
              </div>

              <div className="form-group">
                <label htmlFor="temperaturaAmbiente">{t('ambientTemp')}</label>
                <input
                  type="number"
                  id="temperaturaAmbiente"
                  name="temperaturaAmbiente"
                  value={formData.temperaturaAmbiente}
                  onChange={(e) => setFormData(prev => ({ ...prev, temperaturaAmbiente: parseFloat(e.target.value) || 0 }))}
                  min="-20"
                  max="50"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ambiente">{t('atmosphere')}</label>
                <select
                  id="ambiente"
                  name="ambiente"
                  value={formData.ambiente}
                  onChange={handleInputChange}
                >
                  <option value="DIRECTO">{t('weatherDirect')}</option>
                  <option value="NUBLADO">{t('weatherCloudy')}</option>
                  <option value="LLUVIA">{t('weatherRain')}</option>
                  <option value="TORMENTA">{t('weatherStorm')}</option>
                  <option value="VIENTO">{t('weatherWind')}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="estadoSuperficie">{t('surfaceState')}</label>
                <select
                  id="estadoSuperficie"
                  name="estadoSuperficie"
                  value={formData.estadoSuperficie}
                  onChange={handleInputChange}
                >
                  <option value="DIRECTO">{t('weatherDirect')}</option>
                  <option value="HUMEDO">{t('surfaceWet')}</option>
                  <option value="MOJADO">{t('surfaceSoaked')}</option>
                  <option value="SECO">{t('surfaceDry')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Personal Propio Section */}
        {activeSection === 'personal' && (
          <div className="form-section">
            <div className="section-header">
              <h3>👥 {t('ownStaff')}</h3>
              <button type="button" onClick={addPersonalPropio} className="add-btn">
                + {t('addStaff')}
              </button>
            </div>

            {employees.length === 0 && (
              <div className="alert alert-warning">
                <p>⚠️ {t('noEmployeesCatalog')} <a href="/admin/catalogos/empleados" target="_blank">{t('createEmployees')}</a></p>
              </div>
            )}

            {formData.personalPropio.length === 0 && (
              <p className="empty-section">{t('noStaffAdded')}</p>
            )}

            {formData.personalPropio.map((personal, index) => {
              const selectedEmployee = employees.find(e => e.id === personal.employeeId);
              return (
                <div key={index} className="form-subsection">
                  <div className="subsection-header">
                    <h4>
                      {t('staffItem', { index: index + 1 })}
                      {selectedEmployee && (
                        <span className="employee-badge">
                          {selectedEmployee.attributes.codigo} - {selectedEmployee.attributes.nombre} {selectedEmployee.attributes.apellidos}
                        </span>
                      )}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removePersonalPropio(index)}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <SearchableSelect
                        label={t('catalogEmployees')}
                        required
                        options={employees.map(emp => ({
                          value: emp.id,
                          label: `${emp.attributes.codigo} - ${emp.attributes.nombre} ${emp.attributes.apellidos}`,
                          subtitle: emp.attributes.codigo
                        }))}
                        value={personal.employeeId}
                        onChange={(value) => updatePersonalPropio(index, 'employeeId', value)}
                        placeholder={t('searchEmployee')}
                        emptyMessage={t('noEmployeesCreateFirst')}
                        onCreateNew={() => window.open('/admin/catalogos/empleados', '_blank')}
                        createNewLabel={t('createNewEmployee')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('startTime')}</label>
                      <input
                        type="time"
                        value={personal.horaInicio}
                        onChange={(e) => updatePersonalPropio(index, 'horaInicio', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('endTime')}</label>
                      <input
                        type="time"
                        value={personal.horaFin}
                        onChange={(e) => updatePersonalPropio(index, 'horaFin', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('stopDuration')}</label>
                      <input
                        type="number"
                        value={personal.duracionParadas}
                        onChange={(e) => updatePersonalPropio(index, 'duracionParadas', parseFloat(e.target.value) || 0)}
                        step="0.5"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('grossShift')}</label>
                      <input
                        type="number"
                        value={personal.jornadaBruta}
                        readOnly
                        className="readonly"
                        title={t('autoCalculated')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('netShift')}</label>
                      <input
                        type="number"
                        value={personal.jornadaNeta}
                        readOnly
                        className="readonly"
                        title={t('autoCalculated')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('grouping')}</label>
                      <select
                        value={personal.agrupacion}
                        onChange={(e) => updatePersonalPropio(index, 'agrupacion', e.target.value)}
                      >
                        <option value="EXTENDIDO">{groupingLabel('EXTENDIDO')}</option>
                        <option value="FRESADO">{groupingLabel('FRESADO')}</option>
                        <option value="RIEGOS">{groupingLabel('RIEGOS')}</option>
                        <option value="COMPACTADO">{groupingLabel('COMPACTADO')}</option>
                        <option value="BARRIDO">{groupingLabel('BARRIDO')}</option>
                        <option value="SEÑALIZACION">{groupingLabel('SEÑALIZACION')}</option>
                        <option value="RIEGO_AGLOMERADO">{groupingLabel('RIEGO_AGLOMERADO')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Maquinaria Section */}
        {activeSection === 'maquinaria' && (
          <div className="form-section">
            {/* Maquinaria Propia */}
            <div className="section-header">
              <h3>🚚 {t('ownMachinery')}</h3>
              <button type="button" onClick={addMaquinariaPropia} className="add-btn">
                + {t('addMachinery')}
              </button>
            </div>

            {machinery.length === 0 && (
              <div className="alert alert-warning">
                <p>⚠️ {t('noMachineryCatalog')} <a href="/admin/catalogos/maquinaria" target="_blank">{t('createMachinery')}</a></p>
              </div>
            )}

            {formData.maquinariaPropia.map((maquina, index) => {
              const selectedMachinery = machinery.find(m => m.id === maquina.machineryId);
              return (
                <div key={index} className="form-subsection">
                  <div className="subsection-header">
                    <h4>
                      {t('machineryItem', { index: index + 1 })}
                      {selectedMachinery && (
                        <span className="machinery-badge">
                          {selectedMachinery.attributes.codigo} - {selectedMachinery.attributes.descripcion}
                        </span>
                      )}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeMaquinariaPropia(index)}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <SearchableSelect
                        label={t('catalogMachinery')}
                        required
                        options={machinery.map(m => ({
                          value: m.id,
                          label: `${m.attributes.codigo} - ${m.attributes.descripcion}`,
                          subtitle: m.attributes.tipo
                        }))}
                        value={maquina.machineryId}
                        onChange={(value) => updateMaquinariaPropia(index, 'machineryId', value)}
                        placeholder={t('searchMachinery')}
                        emptyMessage={t('noMachineryCreateFirst')}
                        onCreateNew={() => window.open('/admin/catalogos/maquinaria', '_blank')}
                        createNewLabel={t('createNewMachinery')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('days')}</label>
                      <input
                        type="number"
                        value={maquina.dias}
                        onChange={(e) => updateMaquinariaPropia(index, 'dias', parseInt(e.target.value) || 1)}
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Maquinaria Alquilada */}
            <div className="section-header">
              <h3>🏗️ {t('rentedMachinery')}</h3>
              <button type="button" onClick={addMaquinariaAlquilada} className="add-btn">
                + {t('addRentedMachinery')}
              </button>
            </div>

            {formData.maquinariaAlquilada.map((maquina, index) => (
              <div key={index} className="form-subsection">
                <div className="subsection-header">
                  <h4>{t('rentedMachineryItem', { index: index + 1 })}</h4>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      maquinariaAlquilada: prev.maquinariaAlquilada.filter((_, i) => i !== index)
                    }))}
                    className="remove-btn"
                  >
                    ✕
                  </button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('machine')}</label>
                    <input
                      type="text"
                      value={maquina.maquina}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        maquinariaAlquilada: prev.maquinariaAlquilada.map((item, i) =>
                          i === index ? { ...item, maquina: e.target.value } : item
                        )
                      }))}
                      placeholder="Barredora Axor"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('supplier')}</label>
                    <select
                      value={maquina.supplierId || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        maquinariaAlquilada: prev.maquinariaAlquilada.map((item, i) =>
                          i === index ? { ...item, supplierId: e.target.value ? parseInt(e.target.value) : null } : item
                        )
                      }))}
                    >
                      <option value="">{t('selectSupplier')}</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.attributes.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('days')}</label>
                    <input
                      type="number"
                      value={maquina.dias}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        maquinariaAlquilada: prev.maquinariaAlquilada.map((item, i) =>
                          i === index ? { ...item, dias: parseInt(e.target.value) || 0 } : item
                        )
                      }))}
                      min="0"
                      step="0.5"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('hours')}</label>
                    <input
                      type="number"
                      value={maquina.horas}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        maquinariaAlquilada: prev.maquinariaAlquilada.map((item, i) =>
                          i === index ? { ...item, horas: parseFloat(e.target.value) || 0 } : item
                        )
                      }))}
                      min="0"
                      step="0.5"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Materiales Section */}
        {activeSection === 'materiales' && (
          <div className="form-section">
            {/* Materiales Extendido */}
            <div className="section-header">
              <h3>🏗️ {t('laidMaterials')}</h3>
              <button type="button" onClick={addMaterialExtendido} className="add-btn">
                + {t('addMaterial')}
              </button>
            </div>

            {materials.length === 0 && (
              <div className="alert alert-warning">
                <p>⚠️ {t('noMaterialsCatalog')} <a href="/admin/catalogos/materiales" target="_blank">{t('createMaterials')}</a></p>
              </div>
            )}

            {formData.materialesExtendido.map((material, index) => {
              const selectedMaterial = materials.find(m => m.id === material.materialId);
              return (
                <div key={index} className="form-subsection">
                  <div className="subsection-header">
                    <h4>
                      {t('catalogMaterials')} {index + 1}
                      {selectedMaterial && (
                        <span className="machinery-badge">
                          {selectedMaterial.attributes.codigo} - {selectedMaterial.attributes.descripcion}
                        </span>
                      )}
                    </h4>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        materialesExtendido: prev.materialesExtendido.filter((_, i) => i !== index)
                      }))}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>{t('catalogMaterials')} <span className="required">*</span></label>
                      <select
                        value={material.materialId || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          materialesExtendido: prev.materialesExtendido.map((item, i) =>
                            i === index ? { ...item, materialId: parseInt(e.target.value) } : item
                          )
                        }))}
                        required
                      >
                        <option value="">{t('selectMaterial')}</option>
                        {materials.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.attributes.codigo} - {m.attributes.descripcion}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('extraDescription')}</label>
                      <input
                        type="text"
                        value={material.descripcionExtra}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          materialesExtendido: prev.materialesExtendido.map((item, i) =>
                            i === index ? { ...item, descripcionExtra: e.target.value } : item
                          )
                        }))}
                        placeholder={t('additionalInfo')}
                      />
                    </div>
                    <div className="form-group">
                      <label>M²</label>
                      <input
                        type="number"
                        value={material.m2}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          materialesExtendido: prev.materialesExtendido.map((item, i) =>
                            i === index ? { ...item, m2: parseFloat(e.target.value) || 0 } : item
                          )
                        }))}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>TN</label>
                      <input
                        type="number"
                        value={material.tn}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          materialesExtendido: prev.materialesExtendido.map((item, i) =>
                            i === index ? { ...item, tn: parseFloat(e.target.value) || 0 } : item
                          )
                        }))}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Condiciones y Observaciones Section */}
        {activeSection === 'condiciones' && (
          <div className="form-section">
            <div className="form-group full-width">
              <label htmlFor="observaciones">{t('observations')}</label>
              <textarea
                id="observaciones"
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                rows={6}
                placeholder={t('observationsPlaceholder')}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="images">{t('images')}</label>
              <input
                type="file"
                id="images"
                onChange={(e) => setImages(e.target.files ? Array.from(e.target.files) : [])}
                accept="image/*"
                multiple
              />
              {images.length > 0 && (
                <div className="file-list">
                  {images.map((file, index) => (
                    <span key={index} className="file-name">{file.name}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="attachments">{t('attachments')}</label>
              <input
                type="file"
                id="attachments"
                onChange={(e) => setAttachments(e.target.files ? Array.from(e.target.files) : [])}
                multiple
              />
              {attachments.length > 0 && (
                <div className="file-list">
                  {attachments.map((file, index) => (
                    <span key={index} className="file-name">{file.name}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="cancel-btn"
          disabled={loading}
        >
          {t('cancel')}
        </button>
        <button
          type="submit"
          className="save-btn"
          disabled={loading}
        >
          {loading ? t('savingEllipsis') : (workReport ? t('update') : t('create'))}
        </button>
      </div>
    </form>
  );
};
